import { useLayoutEffect, useRef } from 'react';
import { usePrefersReducedMotion } from '@/hooks/use-prefers-reduced-motion';
import { decidePanelMotion, panelScopeKey, type PanelSnapshot } from './panel-transition-core';
import type { PanelScope } from '../context/types';

type UsePanelTransitionOptions = {
    stepId: string | null;
    stepIndex: number;
    /** The scope the panel is actually *showing*, not the stored one. */
    scope: PanelScope | null;
    /** Motion is pointless or actively wrong right now — see the call site. */
    suspended: boolean;
};

/**
 * Restarts a CSS animation without remounting anything: drop the attribute,
 * force a reflow, set it again.
 *
 * A `key` on the wrapper would replay it too, but it would also unmount the
 * step component underneath and throw away everything it holds — a half-typed
 * field, an open popover — every time someone clicks a block.
 */
const replay = (element: HTMLElement | null, motion: string) => {
    if (!element) return;

    delete element.dataset.panelMotion;
    void element.offsetWidth;
    element.dataset.panelMotion = motion;
};

/**
 * Tells the tools panel to announce that its content changed, and how loudly.
 *
 * The panel is the only always-visible surface for block actions, and it swaps
 * wholesale while the eye is somewhere else — on the step rail, or on the block
 * that was just clicked ~400px away. Without a cue the change is simply missed.
 *
 * Three tiers, cheapest for the most frequent event (see `decidePanelMotion`):
 * the header always fades, the body only moves when the content actually
 * changed, and the edge cue is spent only on a level change.
 *
 * Returns the refs to attach; every visual decision lives in the CSS the data
 * attribute selects, so this hook only ever names what happened.
 */
export function usePanelTransition({
    stepId,
    stepIndex,
    scope,
    suspended,
}: UsePanelTransitionOptions) {
    const headerRef = useRef<HTMLDivElement>(null);
    const bodyRef = useRef<HTMLDivElement>(null);
    const scrollRef = useRef<HTMLDivElement>(null);
    const cueRef = useRef<HTMLSpanElement>(null);
    const snapshotRef = useRef<PanelSnapshot | null>(null);
    const prefersReducedMotion = usePrefersReducedMotion();

    const scopeKey = panelScopeKey(scope);

    useLayoutEffect(() => {
        const previous = snapshotRef.current;
        const current: PanelSnapshot = { stepId, stepIndex, scopeKey };
        snapshotRef.current = current;

        // The first paint is not a transition. While suspended the snapshot is
        // still kept current, so re-expanding the panel does not fire whatever
        // changed while it was closed.
        if (!previous || suspended) return;

        const motion = decidePanelMotion(previous, current);
        if (!motion) return;

        replay(headerRef.current, 'fade');

        if (motion !== 'label') {
            replay(bodyRef.current, prefersReducedMotion ? 'fade' : motion);
            // New content, stale scroll offset. Tier 0 is exempt on purpose:
            // the reader has not moved, so neither should their place.
            if (scrollRef.current) scrollRef.current.scrollTop = 0;
        }

        // Reduced motion keeps the fade — dropping to nothing would lose the
        // signal entirely, which is the problem this exists to solve — but the
        // flashing edge is exactly what `reduce` is asking us not to do.
        if (!prefersReducedMotion && (motion === 'fwd' || motion === 'back')) {
            replay(cueRef.current, 'cue');
        }
    }, [stepId, stepIndex, scopeKey, suspended, prefersReducedMotion]);

    return { headerRef, bodyRef, scrollRef, cueRef };
}
